/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import appOrigin from '@sportninja/common/constants/appOrigin'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import ShareIcon from '../../icons/ShareIcon'
import ShareModal from '../../ShareModal'
import {
  cardStyle,
  cardStyleSelected,
  cardThumbnailStyle,
  cardThumbnailContainerStyle,
  cardContentStyle,
  cardThumbnailEmptyStyle,
  cardTitleStyle,
  cardPeriodStyle,
  playerChipContainerStyle,
  playerChipStyle,
  playerChipImageStyle,
  playerChipNameStyle,
  durationBadgeStyle,
  homeTeamLabelStyle,
  visitorTeamLabelStyle,
} from '../styles'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) {
    return null
  }
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

const PlayerChip = ({ player }) => {
  if (!player?.id) {
    return null
  }

  const name = [player.name_first, player.name_last].filter(Boolean).join(' ')

  return (
    <Link
      to={`${ROUTES.PLAYER_ROOT}/${player.id}`}
      css={playerChipStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={resolveAssetUrl(player.image) || PLAYER_PLACEHOLDER}
        alt={name}
        css={playerChipImageStyle}
        onError={(e) => {
          e.target.src = PLAYER_PLACEHOLDER
        }}
      />
      <span css={playerChipNameStyle}>{name}</span>
    </Link>
  )
}

PlayerChip.propTypes = {
  player: PropTypes.shape({
    id: PropTypes.string,
    name_first: PropTypes.string,
    name_last: PropTypes.string,
    image: PropTypes.string,
  }),
}

/**
 * HighlightCard component for displaying individual highlight items
 */
const shareButtonStyle = css`
  background: ${colors.SECONDARY_150};
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors.SOFT_STEEL};
  }
`

const HighlightCard = ({ highlight, onClick, isPlaying }) => {
  const [isShareOpen, setIsShareOpen] = useState(false)

  const handleClick = () => {
    if (onClick) {
      onClick(highlight)
    }
  }

  const canShare = !!highlight.player_id

  const handleShareClick = (e) => {
    e.stopPropagation()
    setIsShareOpen(true)
  }

  const shareUrl = `${appOrigin}/highlight/${highlight.id}?playerId=${highlight.player_id}`

  const periodText = highlight.period?.name_full || ''
  const clockTime = highlight.period_clock_time
  const showClock = clockTime && clockTime !== '00:00:00'
  const periodInfo = showClock
    ? `${periodText} ${clockTime.split(':').slice(-2).join(':')}`
    : periodText

  const assists = highlight.goal?.assists || []
  const duration = formatDuration(highlight.video_duration)

  return (
    <div css={isPlaying ? cardStyleSelected : cardStyle} onClick={handleClick}>
      <div css={cardThumbnailContainerStyle}>
        <img
          src={
            highlight.thumbnail_url ||
            highlight.thumb ||
            '/images/generic_placeholders/placeholder_video.png'
          }
          alt={`${highlight.title || highlight.type} highlight`}
          css={
            highlight.thumbnail_url || highlight.thumb
              ? cardThumbnailStyle
              : cardThumbnailEmptyStyle
          }
          onError={(e) => {
            e.target.src = '/images/generic_placeholders/placeholder_video.png'
          }}
        />
        {duration && <span css={durationBadgeStyle}>{duration}</span>}
      </div>
      <div css={cardContentStyle}>
        <div css={cardTitleStyle}>{highlight.title || highlight.type}</div>
        {periodInfo && <div css={cardPeriodStyle}>{periodInfo}</div>}
        {(highlight.player || assists.length > 0) && (
          <div css={playerChipContainerStyle}>
            {highlight.player && <PlayerChip player={highlight.player} />}
            {assists.map((assist) => (
              <PlayerChip key={assist.player?.id} player={assist.player} />
            ))}
          </div>
        )}
      </div>
      {canShare && (
        <button css={shareButtonStyle} onClick={handleShareClick}>
          <ShareIcon size={14} />
        </button>
      )}
      {canShare && (
        <div onClick={(e) => e.stopPropagation()}>
          <ShareModal
            isOpen={isShareOpen}
            onClose={() => setIsShareOpen(false)}
            entityType='highlight'
            entityName={highlight.title || highlight.type || 'Highlight'}
            shareUrl={shareUrl}
          />
        </div>
      )}
      {highlight.teamSide && (
        <div
          css={
            highlight.teamSide === 'Home'
              ? homeTeamLabelStyle
              : visitorTeamLabelStyle
          }
        >
          {highlight.teamSide.toUpperCase()}
        </div>
      )}
    </div>
  )
}

HighlightCard.propTypes = {
  highlight: PropTypes.shape({
    id: PropTypes.string.isRequired,
    game_id: PropTypes.string,
    player_id: PropTypes.string,
    team_id: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
    video_url: PropTypes.string,
    thumbnail_url: PropTypes.string,
    video_duration: PropTypes.number,
    period_clock_time: PropTypes.string,
    player: PropTypes.shape({
      id: PropTypes.string,
      name_first: PropTypes.string,
      name_last: PropTypes.string,
      image: PropTypes.string,
    }),
    period: PropTypes.shape({
      name_full: PropTypes.string,
    }),
    goal: PropTypes.shape({
      assists: PropTypes.arrayOf(
        PropTypes.shape({
          player: PropTypes.shape({
            id: PropTypes.string,
            name_first: PropTypes.string,
            name_last: PropTypes.string,
            image: PropTypes.string,
          }),
        })
      ),
    }),
    teamSide: PropTypes.oneOf(['Home', 'Visitor']),
    // Legacy fields
    type: PropTypes.string,
    thumb: PropTypes.string,
    videoUrl: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  isPlaying: PropTypes.bool,
}

HighlightCard.defaultProps = {
  onClick: null,
  isPlaying: false,
}

export default HighlightCard
